import { forwardRef, useId } from 'react';

import { cn } from '@/utils/cn';

import { Button } from './Button';
import { Icon } from './Icon';

// --------------------------------------------------------------------------- //
// Tables
// --------------------------------------------------------------------------- //

/**
 * The frame every data table sits in.
 *
 * A table is the one thing on a page that cannot reflow: squeeze it and the
 * columns crush into unreadable slivers, or the whole page scrolls sideways.
 * This scrolls the table alone. Inside a `Card` it bleeds to the card's edges
 * (`bleed`), so the scrollable area is the full card width rather than a
 * narrower strip inset by the padding.
 *
 * `minWidth` sets the width below which the table scrolls rather than
 * shrinks -- roughly the sum of its columns at a readable size.
 */
export function TableContainer({ children, minWidth = '40rem', bleed = true, className }) {
  return (
    <div className={cn('overflow-x-auto', bleed && '-mx-5 px-5 sm:-mx-6 sm:px-6', className)}>
      <div style={{ minWidth }}>{children}</div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Filters
// --------------------------------------------------------------------------- //

/**
 * One layout for every list page's search and filters.
 *
 * The controls go in as children and flow into as many columns as fit, so a
 * page with two filters and a page with five look like the same product.
 * When filters are applied, `activeCount` says how many and `onReset` puts a
 * single "Reset" beside them -- instead of each page inventing its own, or
 * leaving the admin to clear five dropdowns by hand.
 */
export function FilterBar({ children, activeCount = 0, onReset, className }) {
  return (
    <div
      className={cn('bg-surface border-line rounded-card shadow-card mb-6 border p-4', className)}
      role="search"
    >
      <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
        {children}
      </div>
      {activeCount > 0 && onReset && (
        <div className="border-line mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <p className="text-ink-muted flex items-center gap-1.5 text-xs">
            <Icon name="filter" size={14} />
            {activeCount} filter{activeCount === 1 ? '' : 's'} applied
          </p>
          <Button variant="ghost" size="xs" onClick={onReset}>
            <Icon name="reset" size={14} />
            Reset filters
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * A search box: the field, a magnifier and -- once something is typed -- a
 * clear button. The label is kept for screen readers even when it is not
 * shown, because a placeholder is not a label.
 */
export const SearchInput = forwardRef(function SearchInput(
  { label = 'Search', hideLabel = false, size, value, onChange, onClear, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hasValue = Boolean(value);

  return (
    <div className={cn(hideLabel ? 'space-y-0' : 'space-y-1.5', className)}>
      <label
        htmlFor={inputId}
        className={cn('text-ink block text-sm font-medium', hideLabel && 'sr-only')}
      >
        {label}
      </label>
      <div className="relative">
        <Icon
          name="search"
          size={16}
          className="text-ink-muted pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
        />
        <input
          ref={ref}
          id={inputId}
          type="search"
          value={value}
          onChange={onChange}
          className={cn(
            'border-line bg-surface text-ink placeholder:text-ink-muted/70 rounded-input w-full border',
            'py-2 pr-9 pl-9 text-sm shadow-xs transition hover:border-brand-300',
            size === 'sm' && 'h-8 py-1',
            'focus:border-brand-500 focus:ring-brand-100 focus:ring-4 focus:outline-none',
            // The browser's own clear button would sit on top of ours.
            '[&::-webkit-search-cancel-button]:appearance-none',
          )}
          {...props}
        />
        {hasValue && onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            className="text-ink-muted hover:bg-surface-muted hover:text-ink absolute top-1/2 right-2 grid size-6 -translate-y-1/2 place-items-center rounded transition"
          >
            <Icon name="close" size={14} />
          </button>
        )}
      </div>
    </div>
  );
});
